import { z } from "zod";
import { PasswordErrors } from "../constants";
import { MediaItem } from "@/lib/media/shared";
import { AssistantProfile } from "@/lib/chat-store/types";

export interface AddressResponse {
  ResultItems: Address[];
}

export interface Address {
  PlaceId: string | null;
  PlaceType: string | null;
  Title: string | null;
  Address: {
    Label: string | null;
    Country: {
      Code2: string;
      Code3: string | null;
      Name: string;
    };
    Region: {
      Code: string | null;
      Name: string | null;
    };
    SubRegion: {
      Name: string | null;
    };
    Locality: string;
    District: string;
    PostalCode: string | null;
    Street: string;
    StreetComponents:
      | {
          BaseName: string | null;
          Type: string | null;
          TypePlacement: string | null;
          TypeSeparator: string | null;
          Language: string | null;
        }[]
      | null;
    Block: string | null;
    AddressNumber: string | null;
  };
}

export interface UserData {
  sessionId: string | null;
  idToken: string | null;
  refreshToken: string | null;
  expiryTime: number | null;
  refreshExpiry: number | null;
  sub: string | null;

  currency: string;
  unit_system: string;

  complete_name: string;
  user_type: UserTypes;
  user_photo: string;
  is_admin: boolean;
}

export interface ApiError {
  type: "APIERROR";
  // http_code: number;
  message: string;
  code: string | null;
}

export interface SignInResponse {
  type: "SIGNINRESPONSE";
  code: number;
  message: string;
  access_token: string;
  expires_in: number;
  id_token: string;
  refresh_token: string;
  token_type: string;
  user_sub: string;
}

export interface RefreshResponse {
  type: "REFRESHRESPONSE";
  code: number;
  message: string;
  access_token: string;
  expires_in: number;
  id_token: string;
  token_type: string;
  user_sub: string;
}

export interface HandoffResponse {
  type: "HANDOFFRESPONSE";
  code: number;
  message: string;
  access_token: string;
  expires_in: number;
  id_token: string;
  refresh_token: string;
  token_type: string;
  user_sub: string;
  refresh_expiry: number;
}

export interface SignUpResponse {
  type: "SIGNUPRESPONSE";
  code: number;
  action_performed: string;
  message: string;
}

export interface CookiePreferences {
  unit_system: string;
  currency: string;
  complete_name: string;
  user_type: string;
  photo_url: string;
}

export interface UploadProfilePhotoResponse {
  type: "UPLOADPROFILEPHOTORESPONSE";
  code: number;
  photo_url: string;
  message: string;
}

export enum ListingTypeEnum {
  ESSENTIAL = "essential",
  PLUS = "plus",
  PRIME = "prime",
  SPOTLIGHT = "spotlight",
}

export type EnergyRating = "A" | "B" | "C" | "D" | "E" | "F" | "G" | "N/A";

export interface ListingData {
  listing_id: string;
  title: string;
  purpose: PurposeEnum;
  type: TypeEnum;
  price: number;
  photos: Record<string, MediaItem>; // Changed from string[]
  presentation_video_data: MediaItem; // Replaced presentation_video_data
  brochure: MediaItem;
  number_of_rooms: number;
  number_of_bathrooms: number;
  number_of_full_bathrooms: number;
  number_of_bedrooms: number;
  interior_space: number;
  available_from: string;
  lot_size: number;
  levels: string;
  total_structure_area: number;
  year_built: number;
  buyers_fee: number;
  construction_materials: string;
  map_location: {
    lat: number;
    long: number;
  };
  address: string;
  city: string;
  description: string;
  created_by: string;
  created_at: number;
  status: string;
  is_saved: boolean;
  proximity_listings: ListingCardData[];
  web_tour_url: string;
  has_web_tour: boolean;
  has_presentation_video: boolean;
  has_brochure: boolean;
  reference_id: string;
  epc: EnergyRating;
  listing_type: ListingTypeEnum;
  is_price_per_year: boolean;
  location_on_request: boolean;
  price_on_request: boolean;
  facilities: Facilities;
  view: View;
  orientation: Orientation;
  new_development: boolean;
  is_luxury: boolean;
  office_type: OfficeTypeEnum;
  building_type: BuildingTypeEnum;
  creator: PrivateUserCreator | AgentCreator | AgencyCreator;
  category:
    | ApartmentCategoryEnum
    | HouseCategoryEnum
    | OfficeCategoryEnum
    | LandCategoryEnum
    | null;
  breadcrumbs: Breadcrumb[];
}

export interface Breadcrumb {
  name: string;
  url: string;
  type: string;
  geo_location: {
    lat: number;
    lon: number;
  };
  listing_count: number;
  map_view: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

export interface ListingCardData {
  listing_id: string;
  title: string;
  purpose: string;
  type: string;
  price: number;
  created_by: string;
  photos: Record<string, MediaItem>; // Changed from string[]
  presentation_video_data: MediaItem; // Replaced presentation_video_data
  brochure: MediaItem;
  number_of_rooms: number;
  number_of_bathrooms: number;
  number_of_bedrooms: number;
  interior_space: number;
  available_from: string;
  lot_size: number;
  map_location: {
    lat: number;
    long: number;
  };
  address: string;
  city: string;
  status: string;
  is_saved: boolean;
  version: number;
  web_tour_url: string;
  has_web_tour: boolean;
  has_presentation_video: boolean;
  has_brochure: boolean;
  // epc: EnergyRating;
  listing_type: ListingTypeEnum;
  price_on_request: boolean;
  location_on_request: boolean;
  is_price_per_year: boolean;
  view: View | null;
  orientation: Orientation | null;
  short_description: string;
  creator: CardsCreator;
}

export interface AutocompleteSuggestion {
  slug: string;
  names: Record<string, string>;
  search_urls: Record<string, string>;
  country_code: string;
  country_slug: string;
  for_rent: number;
  for_sale: number;
  house: number;
  apartment: number;
  land: number;
  office: number;
  house_for_sale: number;
  apartment_for_sale: number;
  land_for_sale: number;
  office_for_sale: number;
  house_for_rent: number;
  apartment_for_rent: number;
  land_for_rent: number;
  office_for_rent: number;
  listing_count: number;
  geo_location: {
    lat: number;
    lon: number;
  };
  map_view: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  supported_locales: string[];
  type: string;
  parent_slug: string;
  inverted_boundary_geojson?: any;
}

export interface LocationData {
  slug: string;
  names: Record<string, string>;
  search_urls: Record<string, string>;
  country_code: string;
  country_slug: string;
  for_rent: number;
  for_sale: number;
  house: number;
  apartment: number;
  land: number;
  office: number;
  house_for_sale: number;
  apartment_for_sale: number;
  land_for_sale: number;
  office_for_sale: number;
  house_for_rent: number;
  apartment_for_rent: number;
  land_for_rent: number;
  office_for_rent: number;
  listing_count: number;
  geo_location: {
    lat: number;
    lon: number;
  };
  map_view: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
  supported_locales: string[];
  type: string;
  parent_slug: string;
  inverted_boundary_geojson?: any;
}

export function convertLocationToSuggestion(
  location: LocationData,
): AutocompleteSuggestion {
  return <AutocompleteSuggestion>{
    slug: location.slug,
    names: location.names,
    search_urls: location.search_urls,
    country_code: location.country_code,
    country_slug: location.country_slug,
    for_rent: location.for_rent,
    for_sale: location.for_sale,
    house: location.house,
    apartment: location.apartment,
    land: location.land,
    office: location.office,
    house_for_sale: location.house_for_sale,
    apartment_for_sale: location.apartment_for_sale,
    land_for_sale: location.land_for_sale,
    office_for_sale: location.office_for_sale,
    house_for_rent: location.house_for_rent,
    apartment_for_rent: location.apartment_for_rent,
    land_for_rent: location.land_for_rent,
    office_for_rent: location.office_for_rent,
    listing_count: location.listing_count,
    geo_location: {
      lat: location.geo_location.lat,
      lon: location.geo_location.lon,
    },
    map_view: {
      north: location.map_view.north,
      south: location.map_view.south,
      east: location.map_view.east,
      west: location.map_view.west,
    },
    supported_locales: location.supported_locales,
    type: location.type,
    parent_slug: location.parent_slug,
  };
}

export enum SortingEnum {
  TOPLISTINGS = "top-listings",
  LOWESTPRICE = "lowest-price",
  HIGHESTPRICE = "highest-price",
  NEWESTLISTINGS = "newest-listings",
}

export enum PurposeEnum {
  FORSALE = "for-sale",
  FORRENT = "for-rent",
  DEFAULT = "any",
}

export enum TypeEnum {
  APARTMENT = "apartment",
  HOUSE = "house",
  LAND = "land",
  OFFICE = "office",
  APARTMENT_HOUSE = "apartment-house",
  DEFAULT = "any",
}

export enum ApartmentCategoryEnum {
  STUDIO = "studio",
  LOFT = "loft",
  GARDEN = "garden",
  PENTHOUSE = "penthouse",
  STANDARD = "standard",
}

export enum HouseCategoryEnum {
  DETACHED = "detached",
  TOWNHOUSE = "townhouse",
  DUPLEX = "duplex",
  VILLA = "villa",
  BUNGALOW = "bungalow",
  MANSION = "mansion",
  SEMI_DETACHED = "semi-detached",
}

export enum LandCategoryEnum {
  RESIDENTIAL = "residential",
  COMERCIAL = "commercial",
  AGRICULTURAL = "agricultural",
  INDUSTRIAL = "industrial",
  MIX_USE = "mix-use",
}

export enum OfficeCategoryEnum {
  OFFICE = "office",
  COMERCIAL = "commercial",
  INDUSTRIAL = "industrial",
}

export enum BuildingTypeEnum {
  LOW_RISE = "low-rise",
  MID_RISE = "mid-rise",
  HIGH_RISE = "high-rise",
}

export enum OfficeTypeEnum {
  TRADITIONAL = "traditional",
  CUBICLES = "cubicles",
  OPEN_SPACE = "open-space",
  TEAM_CLUSTER = "team-cluster",
}

export enum UserTypes {
  PRIVATEUSER = "private-user",
  AGENT = "agent",
  AGENCY = "agency",
}

export enum ContactDialogType {
  LISTING = "LISTING",
  TOUR = "TOUR",
  LOCATION_REQUEST = "LOCATION",
  GENERAL = "GENERAL",
}

export enum FurnishingStatusEnum {
  UNFURNISHED = "unfurnished",
  PARTIALLY_FURNISHED = "partially-furnished",
  FULLY_FURNISHED = "fully-furnished",
}

export enum PropertyConditionEnum {
  NEW = "new",
  EXCELLENT = "excellent",
  GOOD = "good",
  NEEDS_RENOVATION = "needs-renovation",
}

export enum HeatingTypeEnum {
  CENTRAL = "central",
  UNDERFLOOR = "underfloor",
  DISTRICT = "district",
  ELECTRIC = "electric",
  NONE = "none",
  OTHER = "other",
}

export interface LandDetails {
  zoning_type: LandCategoryEnum;
  buildable_area_ratio?: number; // Universal for FAR, FSR, POT, or CUT
  road_frontage_length?: number;
  has_gas: boolean;
  has_electricity: boolean;
  has_water: boolean;
  has_sewage: boolean;
}

export interface RentalDetails {
  minimum_lease_months?: number;
  security_deposit_amount?: number;
  pets_allowed: "yes" | "no" | "case-by-case";
}

export interface Facilities {
  has_parking: boolean;
  has_pool: boolean;
  has_fitness_center: boolean;
  has_elevator: boolean;
  has_concierge: boolean;
  has_security: boolean;
  has_public_roof_deck: boolean;
  has_roof_deck: boolean;
  has_garage: boolean;
  has_waterfront: boolean;
  has_balcony: boolean;
  has_furniture: boolean;
  has_garden: boolean;
  has_wine_cellar: boolean;
  has_ac: boolean;
  has_sauna: boolean;
  has_gated_community: boolean;
  is_pet_friendly: boolean;
  has_utilities_included: boolean;
  has_disabled_access: boolean;
  is_ready_to_move_in: boolean;
  has_gas: boolean;
  has_electricity: boolean;
  has_water: boolean;
  has_sewage: boolean;
  has_cafeteria: boolean;
  has_conference_room: boolean;
  has_public_transport: boolean;
  has_hoa: boolean;
  has_intercom: boolean;
  has_dishwasher: boolean;
  has_internet: boolean;
  has_terrace: boolean;
  has_barbecue: boolean;
  has_guest_suites: boolean;
  has_guest_house: boolean;
  has_valet: boolean;
  has_alarm: boolean;
  has_dining_room: boolean;
  has_ev_charging: boolean;
  has_hot_tub: boolean;
  has_washing_machine: boolean;
  has_tennis_court: boolean;
  has_yoga: boolean;
}

export interface SearchFacilities {
  has_parking: boolean;
  has_pool: boolean;
  has_fitness_center: boolean;
  has_elevator: boolean;
  has_concierge: boolean;
  has_security: boolean;
  has_public_roof_deck: boolean;
  has_roof_deck: boolean;
  has_garage: boolean;
  has_waterfront: boolean;
  has_balcony: boolean;
  has_furniture: boolean;
  has_garden: boolean;
  has_wine_cellar: boolean;
  has_ac: boolean;
  has_sauna: boolean;
  has_gated_community: boolean;
  is_pet_friendly: boolean;
  has_utilities_included: boolean;
  has_disabled_access: boolean;
  is_ready_to_move_in: boolean;
  has_gas: boolean;
  has_electricity: boolean;
  has_water: boolean;
  has_sewage: boolean;
  has_cafeteria: boolean;
  has_conference_room: boolean;
  has_public_transport: boolean;
  has_hoa: boolean;
}

export interface View {
  sea_view: boolean;
  mountain_view: boolean;
  countryside_view: boolean;
  city_view: boolean;
}

export interface Orientation {
  north: boolean;
  south: boolean;
  east: boolean;
  west: boolean;
}

export interface SearchUrl {
  sorting: SortingEnum;
  bouding_box: {
    top_left_lat: number;
    top_left_lon: number;
    bottom_right_lat: number;
    bottom_right_lon: number;
    zoom: number;
  };
  center: {
    lat: number;
    lon: number;
  };
  rooms: {
    number: number;
    is_fixed: boolean;
  };
  bedrooms: {
    number: number;
    is_fixed: boolean;
  };
  bathrooms: {
    number: number;
    is_fixed: boolean;
  };
  price: {
    min: number;
    max: number;
  };
  interior_space: {
    min: number;
    max: number;
  };
  lot_size: {
    min: number;
    max: number;
  };
  year_built: {
    min: number;
    max: number;
  };
  maxBuyersFee: number;
  is_luxury: boolean;
  new_development: boolean;
  location_type: string;
  country: string;
}

export const DEFAULT_SEARCH_URL = <SearchUrl>{
  sorting: SortingEnum.TOPLISTINGS,
  bouding_box: {
    top_left_lat: 0,
    top_left_lon: 0,
    bottom_right_lat: 0,
    bottom_right_lon: 0,
    zoom: 3,
  },
  center: {
    lat: 0,
    lon: 0,
  },
  rooms: {
    number: 0,
    is_fixed: false,
  },
  bedrooms: {
    number: 0,
    is_fixed: false,
  },
  bathrooms: {
    number: 0,
    is_fixed: false,
  },
  price: {
    min: 0,
    max: 0,
  },
  interior_space: {
    min: 0,
    max: 0,
  },
  lot_size: {
    min: 0,
    max: 0,
  },
  year_built: {
    min: 0,
    max: 0,
  },
  is_luxury: false,
  new_development: false,
  maxBuyersFee: -1,
  location_type: "",
  country: "",
};

export interface Search {
  location_url: string;
  type: TypeEnum;
  purpose: PurposeEnum;
  sorting: SortingEnum;
  page_number: number;
  bouding_box: {
    top_left_lat: number;
    top_left_lon: number;
    bottom_right_lat: number;
    bottom_right_lon: number;
    zoom: number;
  };
  center: {
    lat: number;
    lon: number;
  };
  rooms: {
    number: number;
    is_fixed: boolean;
  };
  bedrooms: {
    number: number;
    is_fixed: boolean;
  };
  bathrooms: {
    number: number;
    is_fixed: boolean;
  };
  price: {
    min: number;
    max: number;
  };
  interior_space: {
    min: number;
    max: number;
  };
  lot_size: {
    min: number;
    max: number;
  };
  year_built: {
    min: number;
    max: number;
  };
  maxBuyersFee: number;
  is_luxury: boolean;
  category:
    | ApartmentCategoryEnum
    | HouseCategoryEnum
    | LandCategoryEnum
    | OfficeCategoryEnum
    | null;
  building_type: BuildingTypeEnum | null;
  new_development: boolean;
  office_type: OfficeTypeEnum | null;
  has_parking: boolean;
  has_pool: boolean;
  has_fitness_center: boolean;
  has_elevator: boolean;
  has_concierge: boolean;
  has_security: boolean;
  has_public_roof_deck: boolean;
  has_roof_deck: boolean;
  has_garage: boolean;
  has_waterfront: boolean;
  has_balcony: boolean;
  has_furniture: boolean;
  has_garden: boolean;
  has_wine_cellar: boolean;
  has_ac: boolean;
  has_sauna: boolean;
  has_gated_community: boolean;
  is_pet_friendly: boolean;
  has_utilities_included: boolean;
  has_disabled_access: boolean;
  is_ready_to_move_in: boolean;
  has_gas: boolean;
  has_electricity: boolean;
  has_water: boolean;
  has_sewage: boolean;
  has_cafeteria: boolean;
  has_conference_room: boolean;
  has_public_transport: boolean;
  has_hoa: boolean;
  sea_view: boolean;
  mountain_view: boolean;
  countryside_view: boolean;
  city_view: boolean;
  north: boolean;
  south: boolean;
  east: boolean;
  west: boolean;
  location_name: string;
  listing_count: number;
  draw_geojson?: string;
  target_user_id?: string;
  target_agency_id?: string;
}

export interface CardsCreator {
  user_type: UserTypes;
  id: string;
  name: string;
  photo_url: string;
  phone_number: string;
  has_whatsapp_active: boolean;
}

interface BaseCreator {
  id: string;
  name: string;
  photo_url: string;
  active_listings_number: number;
  phone_number: string;
  has_whatsapp_active: boolean;
  is_paid: boolean;
  other_listings: MiniListingCard[] | null;
}

interface MiniListingCard {
  listing_id: string;
  title: string;
  price: number;
  first_photo: MediaItem;
}

export interface PrivateUserCreator extends BaseCreator {
  type: UserTypes.PRIVATEUSER;
}

export interface AgentCreator extends BaseCreator {
  type: UserTypes.AGENT;
  description?: string;
  spoken_languages?: string[];
  years_experience?: number;
  response_time?: number;
  assistant?: AssistantProfile;
  agency: {
    id: string;
    name: string;
    founded_year: number;
    team_size: number;
    photo_url: string;
    website?: string;
    markets?: string[];
    active_listings_number: number;
  };
}

export interface AgencyCreator extends BaseCreator {
  type: UserTypes.AGENCY;
  founded_year: number;
  website?: string;
  markets?: string[];
  address?: string;
  description?: string;
  listing_agents: ListingAgent[];
}

export interface ListingAgent {
  id: string;
  name: string;
  photo_url: string;
  phone_number: string;
  has_whatsapp_active: boolean;
}

export interface ContactDialogUser extends ListingAgent {
  user_type: UserTypes;
}

export interface ResponseTime {
  number: number; //number representing minutes
}

export interface ArticleEditorInitialData {
  id?: string | null;
  title?: string;
  short_description?: string;
  content?: string;
  cover_image?: MediaItem | null;
  location_slug?: string;
  location_name?: string;
  category_slug?: string;
  tags?: string[];
  primary_language?: string;
  language_code?: string;
  status?: string;
  primary_slug?: string;
  slug?: string;
  moderation_message?: string;
  translated_locales?: string[];
  created_at?: string;
  updated_at?: string;
}

export const DEFAULT_SEARCH = <Search>{
  location_url: "",
  type: TypeEnum.DEFAULT,
  purpose: PurposeEnum.DEFAULT,
  sorting: SortingEnum.TOPLISTINGS,
  page_number: 1,
  bouding_box: {
    top_left_lat: 0,
    top_left_lon: 0,
    bottom_right_lat: 0,
    bottom_right_lon: 0,
    zoom: 3,
  },
  center: {
    lat: 0,
    lon: 0,
  },
  rooms: {
    number: 0,
    is_fixed: false,
  },
  bedrooms: {
    number: 0,
    is_fixed: false,
  },
  bathrooms: {
    number: 0,
    is_fixed: false,
  },
  price: {
    min: 0,
    max: 0,
  },
  interior_space: {
    min: 0,
    max: 0,
  },
  lot_size: {
    min: 0,
    max: 0,
  },
  year_built: {
    min: 0,
    max: 0,
  },
  is_luxury: false,
  category: null,
  building_type: null,
  new_development: false,
  office_type: null,
  has_parking: false,
  has_pool: false,
  has_fitness_center: false,
  has_elevator: false,
  has_concierge: false,
  has_security: false,
  has_public_roof_deck: false,
  has_roof_deck: false,
  has_garage: false,
  has_waterfront: false,
  has_balcony: false,
  has_furniture: false,
  has_garden: false,
  has_wine_cellar: false,
  has_ac: false,
  has_sauna: false,
  has_gated_community: false,
  is_pet_friendly: false,
  has_utilities_included: false,
  has_disabled_access: false,
  is_ready_to_move_in: false,
  has_gas: false,
  has_electricity: false,
  has_water: false,
  has_sewage: false,
  has_cafeteria: false,
  has_conference_room: false,
  has_public_transport: false,
  has_hoa: false,
  sea_view: false,
  mountain_view: false,
  countryside_view: false,
  city_view: false,
  north: false,
  south: false,
  east: false,
  west: false,
  maxBuyersFee: -1,
  location_name: "",
  listing_count: 0,
};

export const DAFAULT_FILTERS_FOR_SEARCH: Partial<Search> = {
  rooms: { number: 0, is_fixed: false },
  bedrooms: { number: 0, is_fixed: false },
  bathrooms: { number: 0, is_fixed: false },
  price: { min: 0, max: 0 },
  interior_space: { min: 0, max: 0 },
  lot_size: { min: 0, max: 0 },
  year_built: { min: 0, max: 0 },
  maxBuyersFee: -1,
  has_parking: false,
  has_pool: false,
  has_fitness_center: false,
  has_elevator: false,
  has_concierge: false,
  has_security: false,
  has_public_roof_deck: false,
  has_roof_deck: false,
  has_garage: false,
  has_waterfront: false,
  has_balcony: false,
  has_furniture: false,
  has_garden: false,
  has_wine_cellar: false,
  has_ac: false,
  has_sauna: false,
  has_gated_community: false,
  is_pet_friendly: false,
  has_utilities_included: false,
  has_disabled_access: false,
  is_ready_to_move_in: false,
  has_gas: false,
  has_electricity: false,
  has_water: false,
  has_sewage: false,
  has_cafeteria: false,
  has_conference_room: false,
  has_public_transport: false,
  has_hoa: false,
  sea_view: false,
  mountain_view: false,
  countryside_view: false,
  city_view: false,
  north: false,
  south: false,
  east: false,
  west: false,
};

export interface SubscriptionData {
  client_secret: string;
  product: {
    billing_type: string;
    category: string;
    id: string;
    name: string;
    price: number;
    price_id: string;
  };
  subscription_id: string;
  is_already_paid: boolean | null;
}

export interface Currencies {
  "EUR-USD": number;
  "EUR-EUR": number;
  "EUR-NZD": number;
  "EUR-JPY": number;
  "EUR-SAR": number;
  "EUR-CHF": number;
  "EUR-RON": number;
  "EUR-AED": number;
  "EUR-AUD": number;
  "EUR-HKD": number;
  "EUR-CAD": number;
  "EUR-CNY": number;
  "EUR-GBP": number;
  "EUR-ARS": number;
  "EUR-UAH": number;
  "EUR-THB": number;
  "EUR-DKK": number;
  "EUR-MYR": number;
  "EUR-KRW": number;
  "EUR-ZAR": number;
  "EUR-QAR": number;
  "EUR-ILS": number;
  "EUR-VND": number;
  "EUR-CZK": number;
  "EUR-BHD": number;
  "EUR-BGN": number;
  "EUR-NOK": number;
  "EUR-MXN": number;
  "EUR-EGP": number;
  "EUR-PLN": number;
  "EUR-RUB": number;
  "EUR-SEK": number;
  "EUR-SGD": number;
  "EUR-TRY": number;
  "EUR-INR": number;
  "EUR-BRL": number;
  "USD-EUR": number;
  "EGP-EUR": number;
  "JPY-EUR": number;
  "NZD-EUR": number;
  "SAR-EUR": number;
  "CHF-EUR": number;
  "RON-EUR": number;
  "AED-EUR": number;
  "AUD-EUR": number;
  "HKD-EUR": number;
  "QAR-EUR": number;
  "CAD-EUR": number;
  "CNY-EUR": number;
  "GBP-EUR": number;
  "VND-EUR": number;
  "BGN-EUR": number;
  "SGD-EUR": number;
  "TRY-EUR": number;
  "BRL-EUR": number;
  "RUB-EUR": number;
  "MXN-EUR": number;
  "THB-EUR": number;
  "ARS-EUR": number;
  "PLN-EUR": number;
  "ZAR-EUR": number;
  "KRW-EUR": number;
  "BHD-EUR": number;
  "UAH-EUR": number;
  "MYR-EUR": number;
  "CZK-EUR": number;
  "ILS-EUR": number;
  "NOK-EUR": number;
  "SEK-EUR": number;
  "INR-EUR": number;
  "DKK-EUR": number;
}

export type NotificationRoute = {
  routeKey: string;
  subRouteKey?: string;
  params?: Record<string, string>;
};

export function isSignUpResponse(obj: any): obj is SignUpResponse {
  return (
    obj &&
    typeof obj.code === "number" &&
    typeof obj.action_performed === "string" &&
    typeof obj.message === "string"
  );
}

export interface RecoverPasswordResponse {
  type: "RECOVERPASSWORDRESPONSE";
  code: number;
  message: string;
}

export function isRecoverPasswordResponse(
  obj: any,
): obj is RecoverPasswordResponse {
  return obj && typeof obj.code === "number" && typeof obj.message === "string";
}

export interface ConfirmRecoverPasswordResponse {
  type: "CONFIRMRECOVERPASSWORDRESPONSE";
  code: number;
  message: string;
}

export function isConfirmRecoverPasswordResponse(
  obj: any,
): obj is ConfirmRecoverPasswordResponse {
  return obj && typeof obj.code === "number" && typeof obj.message === "string";
}

export interface SendContactEmailResponse {
  type: "SENDCONTACTEMAILRESPONSE";
  // code: number;
  message: string;
}

export function isSendContactEmailResponse(
  obj: any,
): obj is SendContactEmailResponse {
  return obj && typeof obj.message === "string";
}

export const passwordSchema = z
  .string()
  .trim()
  .min(8, { message: PasswordErrors.TOO_SHORT })
  .max(20, { message: PasswordErrors.TOO_LONG })
  .trim()
  .refine((password) => /[a-z]/.test(password), {
    message: PasswordErrors.ONE_LOWERCASE,
  })
  .refine((password) => /[A-Z]/.test(password), {
    message: PasswordErrors.ONE_UPPERCASE,
  })
  .refine((password) => /[0-9]/.test(password), {
    message: PasswordErrors.ONE_NUMBER,
  })
  .refine((password) => /[!@#$%^&* ]/.test(password), {
    message: PasswordErrors.ONE_SPECIAL,
  });

export const codeSchema = z.string().refine((code) => /^\d{6}$/.test(code), {
  message: "Invalid confirmation code.",
});

export const signInFormSchema = z.object({
  email: z.string().trim().email(),
  password: passwordSchema,
});

export const signUpFormSchema = z.object({
  email: z.string().trim().email(),
  first_name: z
    .string()
    .trim()
    .min(2, { message: "First name should be at least 2 characters long." }),
  last_name: z
    .string()
    .trim()
    .min(2, { message: "Last name should be at least 2 characters long." }),
  password: passwordSchema,
  locale: z.string().trim().optional(),
});

export const recoverPasswordFormSchema = z.object({
  email: z.string().trim().email(),
});

export const confirmSignUpFormSchema = z.object({
  email: z.string().trim().email(),
  confirmation_code: codeSchema,
});

export const confirmRecoverPasswordFormSchema = z.object({
  email: z.string().trim().email(),
  confirmation_code: codeSchema,
  password: passwordSchema,
});
